import { Button, Col, Row, Table, Tag, Typography, message } from "antd"
import React, { useMemo } from "react"
import PageHeader from "../../../components/Elements/PageHeader"
import { MinusCircleFilled, RadiusBottomleftOutlined } from "@ant-design/icons"
import moment from "moment/moment"
import PriceDisplay from "../../../components/utils/PriceDisplay"
import useInteractiveTableFilters from "../../../components/Filters/InteractiveFilters"
import { useAuthContext } from "../../../hooks/useAuthContext"
import { useCollection } from "../../../hooks/useCollection"
import useDeleteDocument from "../../../hooks/useDeleteDocument"
import { doc, updateDoc } from "firebase/firestore"
import { db } from "../../../firebase/config"
import NewOrderDrawer from "./NewOrderDrawer"
import EditTapePriceDrawer from "./EditTapePriceDrawer"

const filterConfig = [
  { key: "client", dataIndex: "client" },
  { key: "product", dataIndex: ["product", "productName"] },
  { key: "filterGroup", dataIndex: "filterGroup" },
]

const { Text, Link } = Typography

export default function TapePrices() {
  const { user } = useAuthContext()
  const { loading, data, refresh } = useCollection(
    "tapePrices",
    ["user", "==", user.uid],
    ["aproved", "==", true]
  )

  const handleEditSuccess = () => {
    if (refresh) refresh()
  }

  // Sort data by creation date (newest first)
  const sortedData = useMemo(() => {
    if (!data) return data
    return [...data].sort((a, b) => b.created.seconds - a.created.seconds)
  }, [data])

  const [appliedFilters, setAppliedFilters, generateFilters] = useInteractiveTableFilters(
    sortedData,
    filterConfig
  )

  const { deleteDocument, deleteDocLoading } = useDeleteDocument()

  const requestForTechincalTape = async (record) => {
    const docref = doc(db, "tapePrices", record.id)

    try {
      await updateDoc(docref, {
        hasTechincalPage: 1,
      }).then(() => {
        message.success("reuqested")
      })
    } catch (error) {
      message.error("something went wrong")
      console.log(error)
    }
  }

  //Controls the state of columns
  const columns = [
    {
      title: "Clients",
      dataIndex: "client",
      align: "center",
      filters: generateFilters("client", "client"),
      onFilter: (value, record) => record.client === value,
      filterSearch: true,
    },
    {
      title: "Product",
      dataIndex: ["product", "productName"],
      filters: generateFilters("product", "productName"),
      align: "center",
      filterSearch: true,
      onFilter: (value, record) => record.product.productName === value,
    },
    {
      title: "Filter Group",
      dataIndex: "filterGroup",
      filterSearch: true,
      align: "center",
      filters: generateFilters("filterGroup", "filterGroup"),
      onFilter: (value, record) => record.filterGroup === value,
      render: (value, record) => {
        if (record.hasFilterGroup) {
          return record.filterGroup
        } else {
          return "none"
        }
      },
    },

    {
      title: "Price",
      dataIndex: "price",
      align: "center",
      sorter: (a, b) => a.price - b.price,
      render: (value, record) => {
        return <PriceDisplay data={record} />
      },
    },
    {
      title: "Tape Width",
      dataIndex: "tapeWidth",
      align: "center",
      sorter: (a, b) => a.tapeWidth - b.tapeWidth,
    },
    {
      title: "Material",
      dataIndex: "material",
      align: "center",
      sorter: (a, b) => a.material - b.material,
    },
    {
      title: "Attachment",
      dataIndex: ["product", "productFileUrl"],
      align: "center",
      render: (value) => (
        <Link type="text" rel="noreferrer" target="_blank" href={value}>
          {value ? "Link" : ""}
        </Link>
      ),
    },
    {
      title: "Created",
      dataIndex: "created",
      align: "center",
      sorter: (a, b) => a.created.seconds - b.created.seconds,
      render: (value) => (
        <>
          <Text>{moment.unix(value.seconds).format("DD/MM/YYYY")}</Text>
        </>
      ),
    },
    {
      title: "Technical",
      dataIndex: "hasTechincalPage",
      align: "center",
      render: (value, record) => {
        if (value === 2) {
          return (
            <Link href={record.technicalPage} target="_blank">
              <Tag color="green">Link</Tag>
            </Link>
          )
        } else if (value === 1) {
          return (
            <Text italic style={{ color: "green" }}>
              progress...
            </Text>
          )
        } else {
          return (
            <Button onClick={() => requestForTechincalTape(record)} size="small">
              Request
            </Button>
          )
        }
      },
    },
    {
      title: "Order",
      align: "center",
      render: (value) => <NewOrderDrawer data={value} />,
    },
    {
      title: "Edit",
      align: "center",
      render: (value) => <EditTapePriceDrawer record={value} onSuccess={handleEditSuccess} />,
    },
    {
      title: "Delete",
      align: "center",
      render: (value) => (
        <>
          <Button
            onClick={() => deleteDocument("tapePrices", value.id)}
            loading={deleteDocLoading}
            size="small"
            icon={<MinusCircleFilled style={{ color: "#f5222d" }} />}>
            Delete
          </Button>
        </>
      ),
    },
  ]

  return (
    <div style={{ padding: 15 }}>
      <Row>
        <Col span={24}>
          <PageHeader text="Tape Products" icon={<RadiusBottomleftOutlined />} />
        </Col>
        <Col span={24}>
          <Table
            appliedFilters={appliedFilters}
            bordered
            size="small"
            columns={columns}
            loading={loading}
            dataSource={sortedData}
            rowKey={(record) => record.id}
            pagination={{ defaultPageSize: "15" }}
            onChange={(pagination, filters) => {
              setAppliedFilters({
                client: filters.client || [],
                product: filters.product || [],
                filterGroup: filters.filterGroup || [],
              })
            }}
          />
        </Col>
      </Row>
    </div>
  )
}
